import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  TextField,
  IconButton,
  Chip,
  Select,
  MenuItem,
  Tooltip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as XLSX from "xlsx";
import styles from "../agent-response.module.scss";
import hcStyles from "./human-capital-agent.module.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  setHumanCapitalFeedback,
  resetHumanCapitalFeedback,
} from "../../../store/filtersSlice";
import { recordAgentAction } from "../../../store/agentSlice";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";

// Proficiency level to text mapping - immutable constant for efficiency
const PROFICIENCY_LEVEL_MAP: Record<number, string> = {
  1: "Beginner",
  2: "Intermediate",
  3: "Experienced",
  4: "Advanced",
  5: "Expert",
} as const;

// Precompute proficiency options and level options for dropdowns
const PROFICIENCY_OPTIONS = Object.values(PROFICIENCY_LEVEL_MAP);
const LEVEL_OPTIONS = [1, 2, 3, 4, 5] as const;

type Skill = {
  skill_name: string;
  skill_description: string;
  proficiency_level: number;
  proficiency: string;
  is_recommended: boolean;
  skill_description_type?: string;
};

type SkillType = {
  skill_type: string;
  skills: Skill[];
};

type JDSummaryItem = {
  id: number;
  skill_type: string;
  summary: string | string[];
};

type RoleData = {
  role: string;
  role_type: string;
  organization_level_role?: string;
  type_of_skills: SkillType[];
  jd_summary?: JDSummaryItem[];
};

type HumanCapitalAgentProps = {
  parsedContent: any;
  agentId?: string;
};

type SkillAction = {
  action: "modify" | null;
  modifiedData?: Partial<Skill>;
};

type ActionMap = {
  [skillTypeIndex: number]: {
    [skillIndex: number]: SkillAction;
  };
};

export default function HumanCapitalAgent({
  parsedContent,
  agentId = "skill_mapping_agent",
}: HumanCapitalAgentProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Determine what to show based on agent ID - memoized to prevent unnecessary recalculations
  const showActionButtons = useMemo(() => agentId === "skill_mapping_agent", [agentId]);
  const showAcceptModify = useMemo(() => agentId === "skill_mapping_agent_modify", [agentId]);
  const showDownloadOnly = useMemo(() => agentId === "skill_mapping_agent_download", [agentId]);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [actionMap, setActionMap] = useState<ActionMap>({});
  const [modifyingSkill, setModifyingSkill] = useState<{
    typeIndex: number;
    skillIndex: number;
  } | null>(null);
  const [modifyData, setModifyData] = useState<Partial<Skill>>({});
  const [clickedButton, setClickedButton] = useState<string | null>(null);

  // Get feedback from Redux (non-message-specific, matches templateReviewerFeedback pattern)
  const humanCapitalFeedback = useSelector(
    (state: RootState) => state.filters.humanCapitalFeedback,
  );

  // Get current agent and loading state for sending messages
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const loading = useSelector((state: RootState) => state.user.loading);

  // Parse the content - handle finalAnswer array with JSON string - use useMemo for stability
  const roleData = useMemo<RoleData | null>(() => {
    try {
      if (
        parsedContent?.finalAnswer &&
        Array.isArray(parsedContent.finalAnswer)
      ) {
        // Parse the JSON string from finalAnswer[0]
        const finalAnswerStr = parsedContent.finalAnswer[0];
        if (typeof finalAnswerStr === "string") {
          return JSON.parse(finalAnswerStr);
        } else {
          return finalAnswerStr;
        }
      } else if (Array.isArray(parsedContent)) {
        return parsedContent[0];
      } else if (parsedContent?.answer) {
        return Array.isArray(parsedContent.answer)
          ? parsedContent.answer[0]
          : parsedContent.answer;
      } else {
        return parsedContent;
      }
    } catch (error) {
      console.error("Error parsing Human Capital data:", error);
      return null;
    }
  }, [parsedContent]);

  // Memoize question extraction
  const question = useMemo(() => parsedContent?.question || "", [parsedContent]);

  // Sort skills by proficiency_level in descending order - memoized for performance
  const sortedRoleData = useMemo<RoleData | null>(() => {
    if (!roleData) return null;

    return {
      ...roleData,
      type_of_skills: roleData.type_of_skills.map(skillTypeGroup => ({
        ...skillTypeGroup,
        skills: [...skillTypeGroup.skills].sort((a, b) =>
          b.proficiency_level - a.proficiency_level
        ),
      })),
    };
  }, [roleData]);

  if (!sortedRoleData) {
    return (
      <div className={styles.pressReleaseContent}>
        <p>No role data available.</p>
      </div>
    );
  }

  // Initialize all sections as expanded by default - only when sortedRoleData changes
  useEffect(() => {
    if (sortedRoleData?.type_of_skills) {
      const allSkillTypes = new Set(
        sortedRoleData.type_of_skills.map((st) => st.skill_type),
      );
      setExpandedSections(allSkillTypes);
    }
  }, [sortedRoleData]);

  // Memoize toggleSection to prevent recreation on every render
  const toggleSection = useCallback((skillType: string) => {
    setExpandedSections(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(skillType)) {
        newExpanded.delete(skillType);
      } else {
        newExpanded.add(skillType);
      }
      return newExpanded;
    });
  }, []);

  // Memoize proficiency color mapping for consistent references
  const getProficiencyColor = useCallback((level: number): string => {
    if (level >= 5) return "#267103";
    if (level >= 4) return "#905d13";
    if (level >= 3) return "#5b6b7a";
    return "#bd1919";
  }, []);

  // Memoize badge style calculation to prevent object recreation
  const getProficiencyBadgeStyle = useCallback((level: number) => {
    if (level >= 5) {
      return {
        color: "#267103",
        backgroundColor: "#dcfada",
        border: "1px solid #39a9064d",
      };
    }
    if (level >= 4) {
      return {
        color: "#905d13",
        backgroundColor: "#faedda",
        border: "1px solid #f5d9a3",
      };
    }
    if (level >= 3) {
      return {
        color: "#5b6b7a",
        backgroundColor: "#e8eaed",
        border: "1px solid #c5cad1",
      };
    }
    return {
      color: "#bd1919",
      backgroundColor: "#fadada",
      border: "1px solid #fadada4d",
    };
  }, []);

  // Helper to get current action for a skill - memoized with actionMap dependency
  const getSkillAction = useCallback((
    typeIndex: number,
    skillIndex: number,
  ): SkillAction => {
    return actionMap[typeIndex]?.[skillIndex] || { action: null };
  }, [actionMap]);

  // Helper to update Redux with grouped data by skill_type
  const updateReduxFeedback = useCallback(
    (newActionMap: ActionMap) => {
      const modify_array: any[] = [];

      // Group modified skills by skill_type
      const groupedModify: { [key: string]: Skill[] } = {};

      sortedRoleData!.type_of_skills.forEach((skillTypeGroup, typeIndex) => {
        const skillType = skillTypeGroup.skill_type;

        skillTypeGroup.skills.forEach((skill, skillIndex) => {
          const action = newActionMap[typeIndex]?.[skillIndex];

          if (action?.action === "modify" && action.modifiedData) {
            if (!groupedModify[skillType]) groupedModify[skillType] = [];
            groupedModify[skillType].push({
              ...skill,
              ...action.modifiedData,
            } as Skill);
          }
        });
      });

      // Convert grouped data to array format
      Object.entries(groupedModify).forEach(([skill_type, skills]) => {
        modify_array.push({ skill_type, skills });
      });

      console.log("HumanCapital - Updating Redux with feedback:", {
        modify_array,
      });

      dispatch(
        setHumanCapitalFeedback({
          modify_array,
        }),
      );
    },
    [dispatch, sortedRoleData],
  );

  // Handle Modify Start - memoized to prevent recreation
  const handleModifyStart = useCallback((
    typeIndex: number,
    skillIndex: number,
    skill: Skill,
  ) => {
    setModifyingSkill({ typeIndex, skillIndex });
    setModifyData({
      skill_name: skill.skill_name,
      skill_description: skill.skill_description,
      proficiency: skill.proficiency,
      proficiency_level: skill.proficiency_level,
      is_recommended: skill.is_recommended,
      skill_description_type: skill.skill_description_type,
    });
  }, []);

  // Handle Modify Save - memoized with dependencies
  const handleModifySave = useCallback(() => {
    if (!modifyingSkill) return;

    const { typeIndex, skillIndex } = modifyingSkill;
    const newActionMap = { ...actionMap };
    if (!newActionMap[typeIndex]) newActionMap[typeIndex] = {};
    newActionMap[typeIndex][skillIndex] = {
      action: "modify",
      modifiedData: modifyData,
    };
    setActionMap(newActionMap);
    updateReduxFeedback(newActionMap);

    setModifyingSkill(null);
    setModifyData({});
  }, [modifyingSkill, actionMap, modifyData, updateReduxFeedback]);

  // Handle Modify Cancel - memoized
  const handleModifyCancel = useCallback(() => {
    setModifyingSkill(null);
    setModifyData({});
  }, []);

  // Handle Undo Action - memoized with dependencies
  const handleUndo = useCallback((typeIndex: number, skillIndex: number) => {
    setActionMap(prev => {
      const newActionMap = { ...prev };
      if (newActionMap[typeIndex]?.[skillIndex]) {
        delete newActionMap[typeIndex][skillIndex];
        if (Object.keys(newActionMap[typeIndex]).length === 0) {
          delete newActionMap[typeIndex];
        }
      }
      return newActionMap;
    });
  }, []);

  // Update Redux feedback when actionMap changes
  useEffect(() => {
    if (Object.keys(actionMap).length > 0) {
      updateReduxFeedback(actionMap);
    }
  }, [actionMap, updateReduxFeedback]);

  // Handle action button clicks - memoized to prevent recreation
  const handleYesProceed = useCallback(() => {
    if (clickedButton) return; // Prevent multiple clicks

    setClickedButton("yes");

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 55, // Human Capital Agent ID
        message: "Yes, proceed",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent?.endpoint || "/stream/human-capital",
        sessionId,
        modify_array: humanCapitalFeedback.modify_array,
      }),
    );

    // Reset feedback after sending
    if (humanCapitalFeedback.modify_array.length > 0) {
      dispatch(resetHumanCapitalFeedback());
    }
  }, [clickedButton, currentAgent, humanCapitalFeedback.modify_array, dispatch]);

  const handleModifyRequest = useCallback(() => {
    if (clickedButton) return; // Prevent multiple clicks

    setClickedButton("modify");

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 55, // Human Capital Agent ID
        message: "I'll modify",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent?.endpoint || "/stream/human-capital",
        sessionId,
        modify_array: humanCapitalFeedback.modify_array,
      }),
    );

    // Reset feedback after sending
    if (humanCapitalFeedback.modify_array.length > 0) {
      dispatch(resetHumanCapitalFeedback());
    }
  }, [clickedButton, currentAgent, humanCapitalFeedback.modify_array, dispatch]);

  const handleProceed = useCallback(() => {
    if (clickedButton) return; // Prevent multiple clicks

    setClickedButton("proceed");

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    console.log("HumanCapital - Sending with modify_array:", humanCapitalFeedback.modify_array);

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 55, // Human Capital Agent ID
        message: "Proceed",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent?.endpoint || "/stream/human-capital",
        sessionId,
        modify_array: humanCapitalFeedback.modify_array,
      }),
    );

    // Reset feedback after sending
    if (humanCapitalFeedback.modify_array.length > 0) {
      dispatch(resetHumanCapitalFeedback());
    }
  }, [clickedButton, currentAgent, humanCapitalFeedback.modify_array, dispatch]);

  // Handle Excel Download
  const handleDownloadExcel = useCallback(() => {
    try {
      const workbook = XLSX.utils.book_new();

      // ========== Summary Sheet ==========
      const summaryData = [
        ["Field", "Value"],
        ["Role", sortedRoleData.role],
        ["Role Type", sortedRoleData.role_type],
        ["Organization Level", sortedRoleData.organization_level_role || "N/A"],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Set column widths for summary sheet
      summarySheet["!cols"] = [
        { wch: 25 }, // Field column
        { wch: 50 }, // Value column
      ];

      // Add summary sheet as first sheet
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      // ========== Skills Sheet ==========
      const worksheetData: any[] = [];

      // Process each skill type
      sortedRoleData.type_of_skills.forEach((skillTypeGroup) => {
        // Add column headers
        worksheetData.push([
          skillTypeGroup.skill_type,
          "Definition",
          "Proficiency",
          "Level",
          "Type",
        ]);

        // Add skills for this type
        skillTypeGroup.skills.forEach((skill) => {
          worksheetData.push([
            skill.skill_name,
            skill.skill_description,
            skill.proficiency,
            skill.proficiency_level,
            skill.skill_description_type || "",
          ]);
        });

        // Add empty row after every skill type
        worksheetData.push([]);
      });

      // Create skills worksheet
      const skillsWorksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths for skills sheet
      skillsWorksheet["!cols"] = [
        { wch: 40 }, // Skill Name
        { wch: 80 }, // Definition
        { wch: 15 }, // Proficiency
        { wch: 10 }, // Level
        { wch: 15 }, // Type
      ];

      // Use role name as sheet name (sanitize for Excel sheet name rules)
      const sheetName = sortedRoleData.role
        .replace(/[:\\/?*\[\]]/g, "") // Remove invalid characters
        .substring(0, 31); // Excel sheet names max 31 chars

      // Add skills worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, skillsWorksheet, sheetName);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = `${sortedRoleData.role.replace(/\s+/g, "_")}_Skills_Mapping_${timestamp}.xlsx`;

      // Record agent action
      dispatch(
        recordAgentAction({
          agentId: 55, // Human Capital Agent ID
          actionType: "download",
          content: JSON.stringify({
            role: sortedRoleData.role,
            role_type: sortedRoleData.role_type,
            organization_level_role: sortedRoleData.organization_level_role,
            skill_types_count: sortedRoleData.type_of_skills.length,
            total_skills: sortedRoleData.type_of_skills.reduce(
              (acc, st) => acc + st.skills.length,
              0,
            ),
          }),
        }),
      );

      // Download file
      XLSX.writeFile(workbook, fileName);

      console.log("Excel downloaded successfully:", fileName);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    }
  }, [sortedRoleData, dispatch]);

  return (
    <div className={styles.pressReleaseContent}>
      {/* Completion Header - Only show for skill_mapping_agent_download */}
      {showDownloadOnly && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            marginBottom: 3,
            padding: 2,
            backgroundColor: "#f0faf9",
            borderRadius: "8px",
            border: "1px solid #00d2aa33",
          }}
        >
          <CheckCircleIcon
            sx={{
              color: "#00d2aa",
              fontSize: 28,
            }}
          />
          <Box>
            <Box
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#004141",
                marginBottom: "4px",
              }}
            >
              Skills Mapping Complete
            </Box>
            <Box
              sx={{
                fontSize: "14px",
                color: "#666",
              }}
            >
              Your role benchmark is ready. Download the Excel file below or
              review the detailed output.
            </Box>
          </Box>
        </Box>
      )}

      {/* Role Header */}
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ marginBottom: "8px", color: "#004141" }}>
          {sortedRoleData.role}
        </h2>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <div
            style={{
              display: "inline-block",
              padding: "4px 12px",
              backgroundColor: "#00d2aa15",
              border: "1px solid #00d2aa33",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#00a888",
            }}
          >
            {sortedRoleData.role_type}
          </div>
          {sortedRoleData.organization_level_role && (
            <div
              style={{
                display: "inline-block",
                padding: "4px 12px",
                backgroundColor: "#f0f4ff",
                border: "1px solid #d0e0ff",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#4a6fa5",
              }}
            >
              {sortedRoleData.organization_level_role}
            </div>
          )}
        </Box>
      </div>

      {/* Download Button - Only show for skill_mapping_agent_download */}
      {showDownloadOnly && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 2 }}>
          <Tooltip title="Download Skills Mapping as Excel">
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExcel}
              className={hcStyles.downloadButton}
            >
              Download Excel
            </Button>
          </Tooltip>
        </Box>
      )}

      {/* Section 1: Detailed Skills and Proficiency Mapping */}
      <Box sx={{ mt: 4 }}>
        <h3
          style={{
            margin: "0 0 20px 0",
            fontSize: "18px",
            fontWeight: "600",
            color: "#004141",
            borderBottom: "2px solid #00a888",
            paddingBottom: "8px",
          }}
        >
          Detailed Skills and Proficiency Mapping
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {sortedRoleData.type_of_skills.map((skillTypeGroup, typeIndex) => {
            const skillTypeCount = skillTypeGroup.skills.filter(
              (s) => s.skill_description_type,
            ).length;

            // Count actions for this skill type
            const actionsInType = actionMap[typeIndex] || {};
            const modifiedCount = Object.values(actionsInType).filter(
              (a) => a.action === "modify",
            ).length;

            return (
              <Box key={typeIndex}>
                {/* Skill Type Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      "& h4": {
                        color: "#00a888",
                      },
                    },
                  }}
                  onClick={() => toggleSection(skillTypeGroup.skill_type)}
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: "#004141",
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#00a888",
                      },
                    }}
                  >
                    {expandedSections.has(skillTypeGroup.skill_type) ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#004141",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {skillTypeGroup.skill_type}
                  </h4>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      padding: "2px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {skillTypeGroup.skills.length} skills
                    {skillTypeCount > 0 && ` • ${skillTypeCount} ${skillTypeGroup.skills[0]?.skill_description_type || ""}`}
                  </div>
                  {modifiedCount > 0 && (
                    <Chip
                      label={`${modifiedCount} modified`}
                      size="small"
                      sx={{
                        backgroundColor: "#e0f7fa",
                        color: "#00a888",
                        fontSize: "11px",
                        height: "20px",
                      }}
                    />
                  )}
                </Box>

                {/* Skills Table - Collapsible */}
                {expandedSections.has(skillTypeGroup.skill_type) && (
                  <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#004141",
                            fontSize: "13px",
                            width: "20%",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          Skill Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#004141",
                            fontSize: "13px",
                            width: showAcceptModify ? "45%" : "55%",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          Definition
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#004141",
                            fontSize: "13px",
                            width: "15%",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          Proficiency
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#004141",
                            fontSize: "13px",
                            width: "10%",
                            textAlign: "center",
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          Level
                        </TableCell>
                        {showAcceptModify && (
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#004141",
                              fontSize: "13px",
                              width: "10%",
                              textAlign: "center",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            Actions
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {skillTypeGroup.skills.map((skill, skillIndex) => {
                        const action = getSkillAction(typeIndex, skillIndex);
                        const isModifying =
                          modifyingSkill?.typeIndex === typeIndex &&
                          modifyingSkill?.skillIndex === skillIndex;
                        const displaySkill =
                          action.action === "modify" && action.modifiedData
                            ? ({ ...skill, ...action.modifiedData } as Skill)
                            : skill;

                        return (
                          <TableRow
                            key={skillIndex}
                            sx={{
                              "&:hover": {
                                backgroundColor: isModifying
                                  ? "#fff"
                                  : "#fafafa",
                              },
                              backgroundColor:
                                action.action === "modify"
                                  ? "#e0f7fa15"
                                  : "#fff",
                              borderLeft:
                                action.action === "modify"
                                  ? "3px solid #00a888"
                                  : skill.skill_description_type
                                    ? "3px solid #00d2aa33"
                                    : "none",
                            }}
                          >
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                color: "#004141",
                                fontWeight: 500,
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              {isModifying ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={modifyData.skill_name || ""}
                                  onChange={(e) =>
                                    setModifyData({
                                      ...modifyData,
                                      skill_name: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.75,
                                      mb: displaySkill.skill_description_type && !action.action ? 0.5 : 0,
                                    }}
                                  >
                                    <span style={{ lineHeight: "1.4" }}>
                                      {skillIndex + 1}. {displaySkill.skill_name}
                                    </span>
                                    {action.action === "modify" && (
                                      <Box
                                        component="span"
                                        sx={{
                                          fontSize: "9px",
                                          fontWeight: 600,
                                          color: "#00a888",
                                          backgroundColor: "#e0f7fa",
                                          borderRadius: "3px",
                                          px: 0.75,
                                          py: 0.375,
                                          lineHeight: 1.2,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.3px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        Modified
                                      </Box>
                                    )}
                                  </Box>
                                  {displaySkill.skill_description_type &&
                                    !action.action && (
                                      <Box
                                        component="span"
                                        sx={{
                                          fontSize: "9px",
                                          fontWeight: 600,
                                          color: "#00a888",
                                          backgroundColor: "#dcfaf4",
                                          border: "1px solid #00d2aa33",
                                          borderRadius: "3px",
                                          px: 0.75,
                                          py: 0.375,
                                          lineHeight: 1.2,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.3px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {displaySkill.skill_description_type}
                                      </Box>
                                    )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "13px",
                                color: "#403f3f",
                                lineHeight: 1.6,
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              {isModifying ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  rows={2}
                                  value={modifyData.skill_description || ""}
                                  onChange={(e) =>
                                    setModifyData({
                                      ...modifyData,
                                      skill_description: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                displaySkill.skill_description
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "13px",
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              {isModifying ? (
                                <Select
                                  size="small"
                                  value={modifyData.proficiency || "Beginner"}
                                  onChange={(e) => {
                                    const selectedProficiency = e.target.value as string;
                                    const level = Object.entries(PROFICIENCY_LEVEL_MAP).find(
                                      ([_, prof]) => prof === selectedProficiency,
                                    )?.[0];
                                    setModifyData({
                                      ...modifyData,
                                      proficiency: selectedProficiency,
                                      proficiency_level: level ? parseInt(level) : 1,
                                    });
                                  }}
                                  sx={{ minWidth: "120px" }}
                                >
                                  {PROFICIENCY_OPTIONS.map((proficiency) => (
                                    <MenuItem key={proficiency} value={proficiency}>
                                      {proficiency}
                                    </MenuItem>
                                  ))}
                                </Select>
                              ) : (
                                <Chip
                                  label={displaySkill.proficiency}
                                  size="small"
                                  sx={{
                                    fontSize: "11px",
                                    fontWeight: 500,
                                    ...getProficiencyBadgeStyle(
                                      displaySkill.proficiency_level,
                                    ),
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                textAlign: "center",
                                color: getProficiencyColor(
                                  displaySkill.proficiency_level,
                                ),
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              {isModifying ? (
                                <TextField
                                  size="small"
                                  value={`${modifyData.proficiency_level || 1}/5`}
                                  disabled
                                  sx={{
                                    width: "70px",
                                    "& .MuiInputBase-input.Mui-disabled": {
                                      WebkitTextFillColor: getProficiencyColor(
                                        modifyData.proficiency_level || 1,
                                      ),
                                      color: getProficiencyColor(
                                        modifyData.proficiency_level || 1,
                                      ),
                                      textAlign: "center",
                                      fontWeight: 600,
                                    },
                                  }}
                                />
                              ) : (
                                `${displaySkill.proficiency_level}/5`
                              )}
                            </TableCell>
                            {showAcceptModify && (
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                  verticalAlign: "top",
                                  py: 2,
                                }}
                              >
                                {isModifying ? (
                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={handleModifySave}
                                      sx={{ color: "#00a888" }}
                                    >
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleModifyCancel}
                                      sx={{ color: "#666" }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ) : action.action ? (
                                  <Button
                                    size="small"
                                    startIcon={<CloseIcon />}
                                    onClick={() =>
                                      handleUndo(typeIndex, skillIndex)
                                    }
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "12px",
                                      color: "#666",
                                      minWidth: "auto",
                                      px: 1,
                                    }}
                                  >
                                    Undo
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() =>
                                      handleModifyStart(
                                        typeIndex,
                                        skillIndex,
                                        skill,
                                      )
                                    }
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "12px",
                                      color: "#00a888",
                                      borderColor: "#00a888",
                                      minWidth: "auto",
                                      px: 1,
                                      "&:hover": {
                                        backgroundColor: "#e0f7fa",
                                        borderColor: "#00a888",
                                      },
                                    }}
                                  >
                                    Modify
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
              </Box>
            );
          })}
        </div>
      </Box>

      {/* Section 2: JD Summary Output */}
      <Box sx={{ mt: 5 }}>
        <h3
          style={{
            margin: "0 0 20px 0",
            fontSize: "18px",
            fontWeight: "600",
            color: "#004141",
            borderBottom: "2px solid #00a888",
            paddingBottom: "8px",
          }}
        >
          JD Summary Output
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {sortedRoleData.jd_summary && sortedRoleData.jd_summary.length > 0 ? (
            sortedRoleData.jd_summary.map((item) => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #0000001a",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                }}
              >
                {/* Skill Type Header */}
                <div
                  style={{
                    padding: "16px 20px",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#004141",
                    }}
                  >
                    {item.skill_type}
                  </h4>
                </div>

                {/* Summary Content */}
                <div
                  style={{
                    padding: "20px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {Array.isArray(item.summary) ? (
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "20px",
                        fontSize: "14px",
                        color: "#403f3f",
                        lineHeight: "1.8",
                      }}
                    >
                      {item.summary.map((point, index) => (
                        <li
                          key={index}
                          style={{
                            marginBottom: "8px",
                          }}
                        >
                          {point}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#403f3f",
                        lineHeight: "1.6",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {item.summary}
                    </p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#666",
                fontSize: "14px",
              }}
            >
              No JD summary data available.
            </div>
          )}
        </div>
      </Box>

      {/* Question - Display if present */}
      {question && <div className={styles.finalQuestion}>{question}</div>}

      {/* Action Buttons - Only show for skill_mapping_agent and when question exists */}
      {showActionButtons && question && (
        <div className={hcStyles.actionButtonsContainer}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleYesProceed}
            disabled={clickedButton !== null || loading}
            className={hcStyles.actionButton}
            sx={{
              "&.Mui-disabled": {
                opacity: 0.6,
                borderColor: "#d0d0d0 !important",
                backgroundColor: "#f5f5f5 !important",
                color: "#a0a0a0 !important",
                cursor: "not-allowed",
                pointerEvents: "none",
                filter: "grayscale(0.3)",
                boxShadow: "none",
              },
            }}
          >
            Yes, proceed
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleModifyRequest}
            disabled={clickedButton !== null || loading}
            className={hcStyles.actionButton}
            sx={{
              "&.Mui-disabled": {
                opacity: 0.6,
                borderColor: "#d0d0d0 !important",
                backgroundColor: "#f5f5f5 !important",
                color: "#a0a0a0 !important",
                cursor: "not-allowed",
                pointerEvents: "none",
                filter: "grayscale(0.3)",
                boxShadow: "none",
              },
            }}
          >
            I'll modify
          </Button>
        </div>
      )}

      {/* Proceed Button - Only show for skill_mapping_agent_modify and when question exists */}
      {showAcceptModify && question && (
        <div className={hcStyles.actionButtonsContainer}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleProceed}
            disabled={clickedButton !== null || loading}
            className={hcStyles.actionButton}
            sx={{
              "&.Mui-disabled": {
                opacity: 0.6,
                borderColor: "#d0d0d0 !important",
                backgroundColor: "#f5f5f5 !important",
                color: "#a0a0a0 !important",
                cursor: "not-allowed",
                pointerEvents: "none",
                filter: "grayscale(0.3)",
                boxShadow: "none",
              },
            }}
          >
            Proceed
          </Button>
        </div>
      )}
    </div>
  );
}
